import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Loader2, Phone, PowerOff, Power, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { adminAxios, useAuth, formatApiErrorDetail } from '../lib/AuthContext';

const DEFAULT_GREETINGS = [
  'Morning Nick.',
  'Hi Nick.',
  'Hi Boss.',
  'Alright Nick.',
  'Morning Boss.',
];

function emptyForm() {
  return { name: '', phone: '', greetings: DEFAULT_GREETINGS.join('\n') };
}

export default function AdminBossUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);

  const loadUsers = async () => {
    try {
      const { data } = await adminAxios.get('/boss/users');
      setUsers(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to load boss users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const greetings = form.greetings.split('\n').map((g) => g.trim()).filter(Boolean);
      await adminAxios.post('/boss/users', {
        name: form.name.trim(),
        phone: form.phone.trim(),
        preferred_greetings: greetings.length ? greetings : DEFAULT_GREETINGS,
      });
      setForm(emptyForm());
      await loadUsers();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await adminAxios.patch(`/boss/users/${u.id}`, { active: !u.active });
      await loadUsers();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to update');
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.phone} from Boss Mode?`)) return;
    try {
      await adminAxios.delete(`/boss/users/${u.id}`);
      await loadUsers();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1829] text-white">
      <header className="border-b border-white/5 bg-[#0d1829]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-400 hover:text-white" data-testid="boss-back-link">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Sophie V2 · Boss Mode</div>
              <div className="text-lg font-semibold mt-0.5" data-testid="boss-page-title">Authorised callers</div>
            </div>
          </div>
          <span className="hidden sm:block text-sm text-slate-300">{user?.email}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <section className="bg-[#1e2d4a]/40 border border-white/10 rounded-2xl p-5">
          <h2 className="text-base font-semibold mb-1">How this works</h2>
          <p className="text-sm text-slate-400 leading-relaxed">
            When someone calls Sophie, she checks their caller ID against this list. If the number matches an active boss
            user, she switches into business-assistant mode — greeting you by name and reading out the brief of new
            enquiries. Anyone else gets the normal customer intake flow.
          </p>
        </section>

        <section className="bg-[#1e2d4a]/40 border border-white/10 rounded-2xl p-5">
          <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add a boss user
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="boss-user-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bn-name" className="text-slate-300 text-sm">Name</Label>
                <Input
                  id="bn-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nick"
                  required
                  className="mt-1 bg-[#0d1829] border-white/10 text-white"
                  data-testid="boss-user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="bn-phone" className="text-slate-300 text-sm">Phone (E.164 or UK)</Label>
                <Input
                  id="bn-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+447990123456 or 07990123456"
                  required
                  className="mt-1 bg-[#0d1829] border-white/10 text-white"
                  data-testid="boss-user-phone-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="bn-greetings" className="text-slate-300 text-sm flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Sophie's greetings (one per line)
              </Label>
              <textarea
                id="bn-greetings"
                value={form.greetings}
                onChange={(e) => setForm({ ...form, greetings: e.target.value })}
                rows={5}
                className="mt-1 w-full bg-[#0d1829] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="boss-user-greetings-input"
              />
              <p className="text-xs text-slate-500 mt-1">She'll randomly pick one each call so it feels human.</p>
            </div>
            {error && (
              <div className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-md px-3 py-2" data-testid="boss-form-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-500" data-testid="boss-user-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Save boss user
            </Button>
          </form>
        </section>

        <section>
          <h2 className="text-base font-semibold mb-3">Current boss users</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-8 justify-center" data-testid="boss-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl" data-testid="boss-empty">
              <Phone className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-300 text-sm">No boss numbers configured yet.</p>
              <p className="text-xs text-slate-500 mt-1">Add the first one above to unlock Sophie's assistant mode.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="boss-users-list">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="bg-[#1e2d4a]/40 hover:bg-[#1e2d4a]/70 border border-white/10 rounded-xl px-4 py-3 flex items-center justify-between gap-3"
                  data-testid={`boss-user-row-${u.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{u.name}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                        u.active
                          ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
                          : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                      }`}>
                        {u.active ? 'active' : 'paused'}
                      </span>
                    </div>
                    <div className="text-sm text-slate-300 mt-0.5 font-mono">{u.phone}</div>
                    {u.lastSeenAt && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        Last call: {new Date(u.lastSeenAt).toLocaleString('en-GB')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(u)}
                      className="text-slate-300 hover:text-white"
                      data-testid={`boss-user-toggle-${u.id}`}
                    >
                      {u.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUser(u)}
                      className="text-red-300 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`boss-user-delete-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
